import React, { useState, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  IconButton,
  Box,
  Typography,
  Alert,
  FormControlLabel,
  Checkbox,
  Tooltip,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { initializeApp } from "firebase/app";
import { getFirestore, collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";

import 'tabulator-tables/dist/css/tabulator.min.css';
import './data.css';

import DataLiveView from "./components/DataLiveView";
import BlockInfoView from "./components/BlockInfoView";

// Your web app's Firebase configuration

const firebaseConfig = {
  apiKey: "AIzaSyAjzpaFsNRic7aea3IBfZfqLiwRMtLAdQg",
  authDomain: "website-payload.firebaseapp.com",
  projectId: "website-payload",
  storageBucket: "website-payload.firebasestorage.app",
  messagingSenderId: "282321022710",
  appId: "1:282321022710:web:9a8ca305783d0ec8d306d7"
};

const app = initializeApp(firebaseConfig);
const db = getFirestore(app);

const lightTheme = createTheme({
  palette: {
    mode: "light",
    background: {
      default: "#202124",
      paper: "#202124",
    },
    text: {
      primary: "#f5f5f5",
    },
    primary: {
      main: "#f5f5f5",
    },
  },
  typography: {
    fontFamily: "sans-serif",
  },
});

const translations = {
  de: {
    emailLabel: "E-Mail",
    description: `Unsere Website wird derzeit gewartet und steht vorübergehend nicht zur Verfügung.\n Bitte versuchen Sie es später erneut.`,
    errorMessage: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
    loading: "Wird geladen...",
    imprintTitle: "Impressum",
    imprintContent: `Newgate GmbH
            Steiningerweg 1
            85748 Garching

            Email: `,
    close: "Schließen"
  },
  en: {
    emailLabel: "Email",
    description: `Our website is currently undergoing maintenance and is temporarily unavailable.\n Please check back later.`,
    errorMessage: "Something went wrong. Please try again.",
    loading: "Loading...",
    imprintTitle: "Imprint",
    imprintContent: `Newgate GmbH
            Steiningerweg 1
            85748 Garching

            Email: `,
    close: "Close"
  },
};

function DataLiveViewWrapper() {
  const navigate = useNavigate();

  const handleBlockClick = (block) => {
    const blockNum = block.toString().replace(/^\//, '');
    navigate(`/data/${blockNum}`);
  };

  return <DataLiveView onBlockClick={handleBlockClick} />;
}

function App() {

  const localPart = 'contact';
  const domain = 'payload.de';
  const email = `${localPart}@${domain}`;

  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState("success");

  const [imprintOpen, setImprintOpen] = useState(false);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Router>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <Box sx={{ paddingTop: "16px", paddingRight: "16px", paddingBottom: "0px", paddingLeft: "16px", minHeight: "100vh" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1rem",
              padding: "0 20px",
            }}
          >
            <a href="/" style={{ display: "flex", alignItems: "center" }}>
              <img
                src="/logo.png"
                alt="Payload"
                style={{ height: "40px", width: "auto", cursor: "pointer" }}
              />
            </a>
            <IconButton
              component="a"
              href="https://x.com/payload_de"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#f5f5f5",
                padding: "4px",
                "&:hover": {
                  backgroundColor: "rgba(245, 245, 245, 0.1)",
                },
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
              </svg>
            </IconButton>
          </Box>

          <Routes>
            <Route path="/" element={<DataLiveViewWrapper />} />
            <Route path="/data" element={<DataLiveViewWrapper />} />
            <Route path="/data/:blockNumber" element={<BlockInfoView />} />
          </Routes>

          <Snackbar
            open={open}
            autoHideDuration={4000}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
          >
            <MuiAlert
              onClose={handleClose}
              severity={severity}
              elevation={6}
              variant="filled"
              sx={{
                width: "100%",
                backgroundColor:
                  severity === "success"
                    ? "#202124"
                    : undefined,
                color:
                  severity === "success"
                    ? "#f5f5f5"
                    : undefined,
              }}
            >
              {message}
            </MuiAlert>
          </Snackbar>

          <Box
            sx={{
              position: 'fixed',
              bottom: 8,
              right: 8,
              color: '#f5f5f5',
              opacity: 0.6,
              display: 'flex',
              gap: 1.5,
              alignItems: 'center',
              background: '#202124',
            }}
          >
            <InfoOutlinedIcon
              onClick={() => setImprintOpen(true)}
              sx={{
                fontSize: '16px',
                color: '#f5f5f5',
                opacity: 0.6,
                cursor: 'pointer',
                '&:hover': { opacity: 1 },
              }}
            />
          </Box>

          <Dialog open={imprintOpen} onClose={() => setImprintOpen(false)} maxWidth="sm" fullWidth slotProps={{
            paper: {
              sx: {
                backgroundColor: '#202124',
                backgroundImage: 'none',
              }
            }
          }}>
            <DialogTitle>Imprint</DialogTitle>
            <DialogContent>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                {`Newgate GmbH
                  Steiningerweg 1
                  85748 Garching

                  Email: ${email}`}
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setImprintOpen(false)}>Close</Button>
            </DialogActions>
          </Dialog>
        </Box>
      </ThemeProvider>
    </Router>
  );
}

export default App;